import React, { useState, useEffect, useRef } from "react";
import { ArrowRight, ChevronLeft, ChevronRight, X, Globe, BookOpen, Award } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Specialist = {
  name: string;
  role: string;
  image: string;
  specialty: string;
  profile: string;
  popup?: {
    title: string;
    quote: string;
    experience: string;
    areas: string;
    achievements: string[];
    training: string[];
    memberships: string[];
    languages: string;
  };
};
const painSpecialists: Specialist[] = [{
  name: "MD Asta Mazeikaite-Baneviciene",
  role: "ANAESTHESIOLOGIST, PAIN SPECIALIST, FOUNDER OF CLINIC AGATAS",
  image: "https://painreliefabroad.ie/wp-content/uploads/2026/04/DSC_4436_2-1-1-scaled.jpg?v=n1zxucengj7",
  specialty: "15 years of experience. Specialising in X-ray-guided and ultrasound-guided procedures, regenerative medicine and evidence-based chronic pain management.",
  profile: "/our-specialists/asta-mazeikaite-baneviciene",
  popup: {
    title: "MD, Pain Specialist, Clinic Founder",
    quote: "When I was young, I did not think about what I wanted to do. I simply knew I would treat people. When I see a person suffering today and I know I can help them, I cannot give up until I succeed. I simply cannot.",
    experience: "15 years of experience since 2011, with earlier experience in obstetric anaesthesiology. Approximately 3,500 patients per year. Clinic Director since 2018.",
    areas: "Consultations, pharmacological and invasive pain management: spinal pain, joint pain, neuropathic pain, occipital neuralgia. Monthly consulting visits at My Family Clinic, Dublin.",
    achievements: ["Founder and Director of Clinic Agatas since 2018", "EU project coordinator at Skane University Hospital, Sweden (2013) and Reykjavik University Hospital, Iceland (2012)", "Researcher in international multicentre study DEX-TRA-04 (2013-2014)", "Published researcher: MDPI 2019, HELLP Syndrome study", "Regular consulting visits to Dublin, Ireland"],
    training: ["Pain School International, Hungary (2023-2024)", "Roma Pain Days, Italy (2023)", "Ultrasound Guided Cryoneurolysis, Austria (2023, 2025)", "Skane University Hospital, Sweden (2013)", "Reykjavik University Hospital, Iceland (2012)", "Multidisciplinary Pain Management, Austria (2017)"],
    memberships: ["European Pain Federation (EFIC)", "European Society of Anaesthesiology (ESA)", "Lithuanian Pain Society (LSD)"],
    languages: "English (Very High), Russian"
  }
}, {
  name: "MD Gediminas Banevicius, PhD",
  role: "ANAESTHESIOLOGIST, PAIN SPECIALIST",
  image: "https://painreliefabroad.ie/wp-content/uploads/2026/04/DSC_3862_2-2-1-scaled.jpg?v=xfaw8rpbhs",
  specialty: "28 years of experience. First in Lithuania to apply spinal anaesthesia for lumbar neurosurgery. Specialising in interventional pain management.",
  profile: "/our-specialists/gediminas-banevicius",
  popup: {
    title: "MD, PhD, Pain Specialist, Lecturer",
    quote: "From childhood I was fascinated by the natural sciences, and I have always felt human pain personally. Today I do what I believe in: that suffering is not the norm, and that almost any pain, however long it has lasted, can be at least partially managed. For every patient I try not only to explain the cause of their pain, but to motivate them to seek treatment, because life without pain holding you back is incomparably better.",
    experience: "28 years of experience since 1998. Approximately 750 patients per year across Kaunas and Klaipeda. First in Lithuania to apply spinal anaesthesia for lumbar neurosurgery (1998).",
    areas: "Anaesthesia for spinal neurosurgery, invasive and pharmacological pain management, consultations.",
    achievements: ["First in Lithuania to apply spinal anaesthesia for lumbar neurosurgery (1998)", "Lecturer at LSMU since 2010", "29+ PubMed publications", "Member of International Society for Ultrasound Guided Cryoneurolysis"],
    training: ["Ultrasound Guided Cryoneurolysis, Austria (2023, 2025)", "Roma Pain Days, Italy (2023)", "Padua University Hospital, Italy", "Landspitali University Hospital, Iceland", "Conference presentations in Germany, France, Finland, Sweden, Denmark, Spain, UK, Estonia, Latvia"],
    memberships: ["European Society of Anaesthesiology (ESA)", "Lithuanian Society of Anaesthesiologists-Reanimatologists", "Lithuanian Pain Society", "Anaesthesiology Club"],
    languages: "English (High), Russian"
  }
}, {
  name: "MD Aida Kinderyte, PhD",
  role: "ANAESTHESIOLOGIST, PAIN SPECIALIST",
  image: "https://painreliefabroad.ie/wp-content/uploads/2026/06/DSC_4159_3-1-scaled.jpg?v=nmyxg18zo",
  specialty: "16 years of experience. PhD in Neuroscience. Specialising in spinal cord stimulation and interventional pain management.",
  profile: "/our-specialists/aida-kinderyte",
  popup: {
    title: "MD, PhD, Pain Specialist",
    quote: "The number of people experiencing pain is growing, and they are getting younger. When I started working in Lithuania, pain medicine was not well developed and there was a shortage of specialists. I wanted to help change that. The most important thing is to see the whole picture and find the real cause, not just treat a single part, because everything in our body is connected.",
    experience: "16 years of experience since 2010. Approximately 2,500 patients per year. PhD in Neuroscience, Biochemistry Laboratory.",
    areas: "Invasive and pharmacological pain management, consultations: spinal cord stimulation, joint and back pain interventions.",
    achievements: ["Keynote presentations at LSMU and national conferences on Spinal Cord Neurostimulation (2015) and Interventional Joint and Back Pain Treatment (2015)", "17+ citations in Web of Science", "ResearchGate and LSMU CRIS research profiles"],
    training: ["Theoretical-Practical Course on Cryoneurolysis, Melilla, Spain (2026)", "Musculoskeletal Illinois Pain Institute, Chicago, USA (2016)", "St George's University London, UK (2013)", "Uppsala Multidisciplinary Pain Centre, Sweden (2018)", "Nijmegen, Netherlands (2018)", "Klagenfurt Pain School, Austria (2016)", "Sardinia, Italy (2009)"],
    memberships: ["Lithuanian Pain Clinics Association", "Musculoskeletal Ultrasound Society"],
    languages: "English (Very High), Russian"
  }
}, {
  name: "MD Grazina Keraitiene",
  role: "ANAESTHESIOLOGIST, PAIN SPECIALIST",
  image: "https://painreliefabroad.ie/wp-content/uploads/2026/04/DSC_4381_2-4-1-scaled.jpg?v=cbtlyxz6irs",
  specialty: "17 years of experience. Specialising in interventional pain procedures and gynaecological pelvic pain.",
  profile: "/our-specialists/grazina-keraitiene",
  popup: {
    title: "MD, Pain Specialist",
    quote: "What matters most to me is doing meaningful work and helping people. The most important outcome is a healthy patient and a good treatment result.",
    experience: "17 years of experience since 2009. Approximately 600 patients per year. 16+ years at LSMU Kaunas Clinics. Joined Clinic Agatas in 2025.",
    areas: "Consultations, pharmacological and invasive pain management.",
    achievements: ["16+ years of clinical practice at LSMU Kaunas Clinics", "European Congress of Anaesthesiology, Barcelona (2013)", "Joined Clinic Agatas 2025"],
    training: ["17th RA-UK ESRA Ultrasound in Pain Medicine Course, London, UK (2026)", "European Congress of Anaesthesiology, Barcelona, Spain (2013)", "Multiple Lithuanian and international conferences (2010-2016)"],
    memberships: ["European Society of Regional Anaesthesia and Pain Medicine (ESRA)"],
    languages: "English (Upper Intermediate), Russian"
  }
}, {
  name: "MD Algirdas Sukys",
  role: "SURGEON, PAIN SPECIALIST",
  image: "https://painreliefabroad.ie/wp-content/uploads/2026/04/DSC_5244_2-1-1-1-scaled.jpg?v=rt7otemogfa",
  specialty: "40+ years of experience. Certified ultrasound specialist since 1989. Specialising in ultrasound-guided pain procedures and interventional pain management.",
  profile: "/our-specialists/algirdas-sukys",
  popup: {
    title: "MD, Pain Specialist, Ultrasound Specialist",
    quote: "Surgical and ultrasound experience complement each other perfectly in reducing patient pain. The most important thing is the result: that the patient is free from pain, both physically and emotionally.",
    experience: "40+ years of experience since 1985. Certified ultrasound specialist since 1989. Over 5,000 procedures performed.",
    areas: "Ultrasound diagnostics of joints, ligaments, tendons and nerves. Ultrasound-guided pain procedures and nerve blocks. Cryoneurolysis, spasticity treatment, regenerative therapy. Consultations.",
    achievements: ["Certified ultrasound specialist since 1989", "Founder and former director of pain clinic in Panevezys", "Over 5,000 procedures performed", "Previously worked at Panevezys and Siauliai hospitals"],
    training: ["Pain School International, Hungary (2023-2024)", "Master Cryoneurolysis, Advanced Workshop, Copenhagen (2025)", "London Pain Forum (2019)"],
    memberships: ["Lithuanian Ultrasound Association", "Lithuanian Pain Society", "Regional Sonographers Union (RSUUD)"],
    languages: "English, German"
  }
}, {
  name: "MD Jurgita Jasulaitiene",
  role: "ANAESTHESIOLOGIST, PAIN SPECIALIST",
  image: "https://painreliefabroad.ie/wp-content/uploads/2026/04/DSC_5087_2-1-2-scaled.jpg?v=kao2isgemrj",
  specialty: "14 years of experience. Specialising in interventional pain management, chronic pain and spinal pathologies.",
  profile: "/our-specialists/jurgita-jasulaitiene",
  popup: {
    title: "MD, Pain Specialist",
    quote: "What I enjoy about this work is the variety: interventional treatment, very little monotony. Above all, it is about achieving a good result and improving the quality of life for every patient.",
    experience: "14 years of experience since 2012. Approximately 450 patients per year at Clinic Agatas. Clinical practice across multiple hospitals in Lithuania.",
    areas: "Consultations, invasive and pharmacological pain management: spinal stenosis, chronic pain.",
    achievements: ["ESRA Annual Congress, Paris, France (2023)", "15+ years of clinical experience across multiple hospitals"],
    training: ["17th RA-UK ESRA Ultrasound in Pain Medicine Course, London, UK (2026)", "Roma Pain Days (2026)", "6th World Congress on Regional Anaesthesia and Pain Medicine, Paris, France (2023)", "Chronic pain management internship, LSMU (2020)", "Chronic pain management internship, Vilnius University (2022)"],
    memberships: ["Lithuanian Society of Anaesthesiologists (LARD)", "Lithuanian Pain Society (LSD)", "European Society of Regional Anaesthesia and Pain Medicine (ESRA)"],
    languages: "English (Upper Intermediate), Russian"
  }
}];
function SpecialistModal({
  specialist,
  onClose
}: {
  specialist: Specialist;
  onClose: () => void;
}) {
  const p = specialist.popup;
  if (!p) return null;
  return <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 md:p-8" onClick={onClose}>
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-full md:max-w-[50vw] max-h-[90vh] overflow-y-auto border-4 border-[#264256]" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-[#eefaf4] flex items-center justify-center hover:bg-[#a9e5c9] transition-colors">
          <X className="w-4 h-4 text-[#264256]" />
        </button>
        <div className="flex flex-col md:flex-row">
          <div className="w-full md:w-[240px] flex-shrink-0 md:sticky md:top-0 md:h-fit">
            <img src={specialist.image} alt={specialist.name} className="w-full object-cover object-center md:rounded-tl-xl md:rounded-bl-xl rounded-tl-xl rounded-tr-xl" style={{
            minHeight: "280px",
            maxHeight: "480px"
          }} />
          </div>
          <div className="flex-1 p-6 flex flex-col gap-4 overflow-y-auto">
            <div>
              <h2 className="font-default font-bold text-[#264256] text-xl leading-tight">{specialist.name}</h2>
              <p className="font-default text-[#65d19d] text-sm font-semibold mt-1">{p.title}</p>
            </div>
            <blockquote className="border-l-4 border-[#65d19d] pl-4">
              <p className="font-default text-[#264256]/80 text-sm leading-relaxed italic">"{p.quote}"</p>
            </blockquote>
            <div className="flex flex-col gap-1">
              <span className="font-default font-bold text-[#264256] text-xs uppercase tracking-wider">Experience</span>
              <p className="font-default text-[#264256] text-sm leading-relaxed">{p.experience}</p>
            </div>
            <div className="flex flex-col gap-1">
              <span className="font-default font-bold text-[#264256] text-xs uppercase tracking-wider">Areas of Practice</span>
              <p className="font-default text-[#264256] text-sm leading-relaxed">{p.areas}</p>
            </div>
            <div className="flex flex-col gap-2">
              <span className="font-default font-bold text-[#264256] text-xs uppercase tracking-wider flex items-center gap-1">
                <Award className="w-3.5 h-3.5" /> Key Achievements
              </span>
              <ul className="flex flex-col gap-1">
                {p.achievements.map((item, i) => <li key={i} className="flex items-start gap-2">
                    <span className="text-[#65d19d] shrink-0 mt-0.5">+</span>
                    <span className="font-default text-[#264256] text-sm">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="flex flex-col gap-2">
              <span className="font-default font-bold text-[#264256] text-xs uppercase tracking-wider flex items-center gap-1">
                <Globe className="w-3.5 h-3.5" /> International Training
              </span>
              <ul className="flex flex-col gap-1">
                {p.training.map((item, i) => <li key={i} className="flex items-start gap-2">
                    <span className="text-[#65d19d] shrink-0 mt-0.5">+</span>
                    <span className="font-default text-[#264256] text-sm">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="flex flex-col gap-2">
              <span className="font-default font-bold text-[#264256] text-xs uppercase tracking-wider flex items-center gap-1">
                <BookOpen className="w-3.5 h-3.5" /> Professional Memberships
              </span>
              <ul className="flex flex-col gap-1">
                {p.memberships.map((item, i) => <li key={i} className="flex items-start gap-2">
                    <span className="text-[#65d19d] shrink-0 mt-0.5">+</span>
                    <span className="font-default text-[#264256] text-sm">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="flex flex-col gap-1 pt-2 border-t border-[#f1f3f5]">
              <span className="font-default font-bold text-[#264256] text-xs uppercase tracking-wider">Languages</span>
              <span className="font-default text-[#264256] text-sm">{p.languages}</span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
export default function Prototype() {
  const {
    ref
  } = useRevealOnScroll();
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeModal, setActiveModal] = useState<Specialist | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [cardW, setCardW] = useState(400);
  useEffect(() => {
    const update = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        setCardW(mobile ? containerWidth : 400);
      }
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  const gap = isMobile ? 16 : 32;
  const visibleCount = isMobile ? 1 : 2;
  const maxIndex = Math.max(0, painSpecialists.length - visibleCount);
  return <section data-section-id="20593" id="pain-specialists" ref={ref} className="bg-background relative w-full py-12 md:py-20 overflow-hidden">
      {activeModal && <SpecialistModal specialist={activeModal} onClose={() => setActiveModal(null)} />}
      <div className="max-w-7xl mx-auto px-4 md:px-12">
        <div className="flex items-center justify-between mb-8 md:mb-10">
          <h3 className="font-default text-2xl md:text-3xl font-bold text-primary">Interventional pain management specialists</h3>
          <div className="flex gap-3">
            <button onClick={() => setCurrentIndex(i => Math.max(0, i - 1))} disabled={currentIndex === 0} className="w-10 h-10 md:w-12 md:h-12 rounded-md bg-[#a9e5c9] flex items-center justify-center disabled:opacity-30 hover:bg-[#65d19d] transition-colors">
              <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-[#264256]" />
            </button>
            <button onClick={() => setCurrentIndex(i => Math.min(maxIndex, i + 1))} disabled={currentIndex >= maxIndex} className="w-10 h-10 md:w-12 md:h-12 rounded-md bg-[#a9e5c9] flex items-center justify-center disabled:opacity-30 hover:bg-[#65d19d] transition-colors">
              <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-[#264256]" />
            </button>
          </div>
        </div>
        <div ref={containerRef} className="relative w-full overflow-hidden py-4">
          <div className="flex transition-transform duration-500 ease-in-out" style={{
          gap: `${gap}px`,
          transform: `translateX(-${currentIndex * (cardW + gap)}px)`
        }}>
            {painSpecialists.map((person, i) => <div key={person.name} data-index={i} className="group relative flex-shrink-0" style={{
            width: isMobile ? `${cardW}px` : "400px"
          }}>
                <Card className="h-full overflow-hidden bg-card transition-all duration-500 rounded-lg shadow-sm hover:shadow-md hover:-translate-y-1 border-border/40">
                  <div className="relative aspect-[3/4] overflow-hidden w-full">
                    <img src={person.image} alt={person.name} className="h-full w-full object-cover object-top transition-all duration-700 ease-in-out scale-100 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="absolute inset-0 flex flex-col items-center justify-end md:justify-center gap-3 pb-5 md:pb-0 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all duration-500 z-10">
                      {person.popup ? <button onClick={() => setActiveModal(person)} className="inline-flex items-center gap-2 px-5 py-2.5 bg-background/90 backdrop-blur-md border border-border/50 rounded-full text-sm font-default font-medium text-primary hover:bg-primary hover:text-white transition-colors shadow-lg">
                          View Profile <ArrowRight className="w-4 h-4" />
                        </button> : <a href={person.profile} className="inline-flex items-center gap-2 px-5 py-2.5 bg-background/90 backdrop-blur-md border border-border/50 rounded-full text-sm font-default font-medium text-primary hover:bg-primary hover:text-white transition-colors shadow-lg">
                          View Profile <ArrowRight className="w-4 h-4" />
                        </a>}
                    </div>
                  </div>
                  <CardContent className="pt-4 pb-6 px-4 md:pt-6 md:pb-8 md:px-6 text-center relative z-20 bg-card border-t border-border/20">
                    <h3 className="font-default text-xl md:text-2xl font-semibold text-primary mb-2">{person.name}</h3>
                    <div className="flex flex-col gap-1 items-center">
                      <span className="text-xs md:text-sm font-default font-bold text-accent-foreground uppercase tracking-wider">{person.role}</span>
                      <p className="font-default text-sm text-gray-700 mt-2 md:mt-3 leading-relaxed">{person.specialty}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}